import { useState, useEffect, useRef } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { EditPage, EditPagePanel, EditPageTextField, EditPageSelect, EditPageCheckbox } from './../../../../common/EditPage';
import { useParams, useNavigate } from 'react-router-dom';
import { getOne, update } from './../../../../common/v4/BackendProvider';
import { toast } from 'react-toastify';

const ClientAccountSmppAccountsEdit = () => {
    const [metadata, setMetadata] = useState({});
    const [data, setData] = useState({
        ATRUSERNAME: '',
        ATRPASSWORD: '',
        ATRPRODUCTID: '',
        ATRBIND: 'transceiver',
        ATRCODING: '0',
        ATRSTATUS: true
    });
    const [smppStatus, setSmppStatus] = useState([]);
    const [loadingStatus, setLoadingStatus] = useState(false);
    const { account_id, id } = useParams();
    const navigate = useNavigate();
    const refreshIntervalRef = useRef(null);
    
    useEffect(() => {
        // Fetch the existing SMPP account data
        getOne(`/admin/clients/clients/${account_id}/smppaccounts/`, id).then(response => {
            setData(response.data.data);
            setMetadata(response.data.metadata);
        }).catch(error => {
            toast.error("Error fetching SMPP account: " + error.message, {});
        });
    }, [account_id, id]);

    // Function to fetch SMPP status
    const fetchSmppStatus = async () => {
        if (!data.ATRUSERNAME) return;
        
        setLoadingStatus(true);
        try {
            const response = await fetch(`/api/v2/admin/smpp/status/${data.ATRUSERNAME}`);
            const result = await response.json();
            if (result.result === 'OK') {
                setSmppStatus(result.data.connections || []);
            }
        } catch (error) {
            console.error('Error fetching SMPP status:', error);
            toast.error('Failed to fetch SMPP status');
        } finally {
            setLoadingStatus(false);
        }
    };

    // Fetch SMPP status when username changes and set up auto-refresh
    useEffect(() => {
        if (data.ATRUSERNAME) {
            // Initial fetch
            fetchSmppStatus();
            
            // Set up auto-refresh every 60 seconds
            refreshIntervalRef.current = setInterval(() => {
                fetchSmppStatus();
            }, 60000); // 60 seconds
            
            // Cleanup function to clear interval when component unmounts or username changes
            return () => {
                if (refreshIntervalRef.current) {
                    clearInterval(refreshIntervalRef.current);
                    refreshIntervalRef.current = null;
                }
            };
        } else {
            // Clear interval if no username
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
                refreshIntervalRef.current = null;
            }
        }
    }, [data.ATRUSERNAME]);

    const handleSave = () => {
        update(`/admin/clients/clients/${account_id}/smppaccounts/`, id, {data: data}).then(() => {
            toast.success('SMPP account updated successfully');
            navigate(`/clients/clients/${account_id}/smppaccounts`);
        }).catch(error => {
            toast.error("Error updating SMPP account: " + error.message, {});
        });
    };

    const handleCancel = () => {
        navigate(`/clients/clients/${account_id}/smppaccounts`);
    }

    const onChange = (id, value) => {
        setData({ ...data, [id]: value });
    }

    return (
        <>
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                closeOnClick={true}
                pauseOnHover={true}
                draggable={true}
                progress="undefined"
            />
            <EditPage handleSave={handleSave} handleCancel={handleCancel}>
                <EditPagePanel>
                    <EditPageTextField 
                        label="SMPP Username" 
                        id="ATRUSERNAME" 
                        onChange={onChange} 
                        value={data.ATRUSERNAME}
                        placeholder="smppusername"
                    />
                    <EditPageTextField 
                        label="Password" 
                        id="ATRPASSWORD" 
                        onChange={onChange} 
                        value={data.ATRPASSWORD}
                        type="password"
                        placeholder="Enter password"
                    />
                    <EditPageSelect 
                        label="Rate Profile" 
                        id="ATRPRODUCTID" 
                        onChange={onChange} 
                        value={data.ATRPRODUCTID}
                        options={metadata?.products?.values || {}}
                        placeholder="Select product"
                        nullable
                    />
                    <EditPageSelect 
                        label="Bind" 
                        id="ATRBIND" 
                        onChange={onChange} 
                        value={data.ATRBIND}
                        options={{
                            'transceiver': 'Transceiver',
                            'transmitter': 'Transmitter',
                            'receiver': 'Receiver'
                        }}
                        placeholder="Select bind type"
                    />
                    <EditPageSelect 
                        label="Coding" 
                        id="ATRCODING" 
                        onChange={onChange} 
                        value={data.ATRCODING}
                        options={{
                            '0': 'Default',
                            '1': 'IA5 (ASCII)',
                            '3': 'Latin 1',
                            '8': 'UCS2'
                        }}
                        placeholder="Select coding"
                    />
                    <EditPageCheckbox 
                        label="Enabled" 
                        id="ATRSTATUS" 
                        onChange={onChange} 
                        value={data.ATRSTATUS}
                    />
                </EditPagePanel>
                
                {/* SMPP Status Table */}
                {data.ATRUSERNAME && (
                    <EditPagePanel>
                        <div>
                            <h4>SMPP Connection Status</h4>
                            <div className="table-responsive">
                                <table className="table table-bordered table-striped">
                                    <thead>
                                        <tr>
                                            <th>Server Name</th>
                                            <th>Active Connections</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loadingStatus ? (
                                            <tr>
                                                <td colSpan="2" className="text-center">
                                                    <div className="spinner-border spinner-border-sm" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                    Loading SMPP status...
                                                </td>
                                            </tr>
                                                                            ) : smppStatus.length === 0 ? (
                                        <tr>
                                            <td colSpan="2" className="text-center text-muted">
                                                <span className="badge" style={{ backgroundColor: '#dc3545', color: '#dc3545', marginRight: '12px' }}>
                                                    ●
                                                </span>
                                                No active connections found
                                            </td>
                                        </tr>
                                    ) : (
                                            smppStatus.map((server, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <strong>{server.server_name}</strong>
                                                    </td>
                                                    <td>
                                                        {server.connections && server.connections.length > 0 ? (
                                                            <ul className="list-unstyled mb-0">
                                                                {server.connections.map((connection, connIndex) => (
                                                                    <li key={connIndex} className="mb-1">
                                                                        <span className="badge" style={{ backgroundColor: '#198754', color: '#198754', marginRight: '12px' }}>
                                                                            ●
                                                                        </span>
                                                                        <code>{connection}</code>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        ) : (
                                                            <span className="text-muted">
                                                                <span className="badge" style={{ backgroundColor: '#dc3545', color: '#dc3545', marginRight: '12px' }}>
                                                                    ●
                                                                </span>
                                                                No active connections
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <div className="mt-2">
                                <small className="text-muted">
                                    <i className="fas fa-sync-alt me-1"></i>
                                    Auto-refreshing every 60 seconds
                                </small>
                            </div>
                        </div>
                    </EditPagePanel>
                )}
            </EditPage>
        </>
    )
}   

export default ClientAccountSmppAccountsEdit;